<script setup lang="ts">
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import type { Column } from '@tanstack/vue-table';
    import { formatCurrency } from '@/utils/helpers';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';

    interface Props {
        column: Column<any, unknown>;
        modelValue?: [number | undefined, number | undefined] | undefined;
    }

    interface Emits {
        (e: 'update:modelValue', value: [number | undefined, number | undefined] | undefined): void;
    }

    const props = defineProps<Props>();
    const emit = defineEmits<Emits>();
    const { t } = useI18n();

    const fromValue = computed<number | undefined>({
        get() {
            return props.modelValue?.[0];
        },
        set(value: number | undefined) {
            updateFilterValue(value, toValue.value);
        },
    });

    const toValue = computed<number | undefined>({
        get() {
            return props.modelValue?.[1];
        },
        set(value: number | undefined) {
            updateFilterValue(fromValue.value, value);
        },
    });

    const updateFilterValue = (from: number | undefined, to: number | undefined) => {
        if (from === undefined && to === undefined) {
            emit('update:modelValue', undefined);
            return;
        }

        emit('update:modelValue', [from, to]);
    };

    const minPlaceholder = computed(() => {
        let min: number | string = props.column.getFacetedMinMaxValues()?.[0] ?? 0;

        if (props.column.columnDef.meta?.filterType === 'currency') {
            min = formatCurrency(min);
        }

        return `Min (${min})`;
    });

    const maxPlaceholder = computed(() => {
        let max: number | string = props.column.getFacetedMinMaxValues()?.[1] ?? 0;

        if (props.column.columnDef.meta?.filterType === 'currency') {
            max = formatCurrency(max);
        }

        return `Max (${max})`;
    });
</script>

<template>
    <div class="flex gap-2">
        <div class="flex flex-col gap-2">
            <Label>{{ t('forms.from') }}</Label>
            <component
                :is="
                    props.column.columnDef.meta?.filterType === 'currency'
                        ? ThousandSeparatorInput
                        : Input
                "
                v-model="fromValue"
                :type="props.column.columnDef.meta?.filterType === 'currency' ? 'text' : 'number'"
                step="any"
                :placeholder="minPlaceholder"
                with-decimals
            />
        </div>

        <div class="flex flex-col gap-2">
            <Label>{{ t('forms.to') }}</Label>
            <component
                :is="
                    props.column.columnDef.meta?.filterType === 'currency'
                        ? ThousandSeparatorInput
                        : Input
                "
                v-model="toValue"
                :type="props.column.columnDef.meta?.filterType === 'currency' ? 'text' : 'number'"
                step="any"
                :placeholder="maxPlaceholder"
                with-decimals
            />
        </div>
    </div>
</template>
